<template>
  <div class="min-h-screen">
    <SiteHeader :page-name="'Entities List'" />
    <div class="mt-4 w-full flex flex-col m-auto" style="height: 90vh">
      <Toolbar pt:root:class="mx-4 py-2">
        <template #start>
          <div class="flex flex-row py-2 gap-2">
            <SearchBar v-model:search-text="searchText" show-refresh @input="onFilterTextBoxChanged" @refresh="populateEntities"></SearchBar>
            <ToolbarButtons label="Entity" :show-add="userPermissions.addEntity" @add="toggleMode('Add')"></ToolbarButtons>
            <ReportsListButton :context="'ALL_ENTITIES'"></ReportsListButton>
          </div>
        </template>
        <template #end>
          <SplitButton
            :label="selectedEntity ? getEntityDisplay(selectedEntity) : 'Selected Entity'"
            severity="surface"
            menuButtonIcon="pi pi-cog text-xl"
            class="h-10"
            :model="menuItems"
            :disabled="!selectedEntity"
            @click="display = true"
          />
        </template>
      </Toolbar>
      <div style="width: 100%; flex: 1 1 auto">
        <ag-grid-vue
          theme="legacy"
          :rowData="entities"
          :columnDefs="colDefs"
          :defaultColDef="defaultColDefs"
          :autoSizeStrategy="{ type: 'fitGridWidth' }"
          :suppressCellFocus="true"
          :suppressDragLeaveHidesColumns="true"
          class="mx-4 py-3 h-full"
          :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
          rowSelection="single"
          @row-selected="selectEntity"
          @row-data-updated="resize"
          @grid-size-changed="resize"
          @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
        >
        </ag-grid-vue>
      </div>
    </div>
    <TSDialog label="Entity Detail" v-model:visible="display" v-if="selectedEntity && display" icon="info-circle">
      <template #content>
        <EntityDetail :entity="selectedEntity" />
      </template>
    </TSDialog>
    <TSDialog
      v-if="mode"
      :label="mode + ' Entity'"
      v-model:visible="displayModal"
      v-on:after-hide="modalClose()"
      :icon="mode === 'Edit' ? 'user-edit' : mode === 'Copy' ? 'users' : 'user-plus'"
      size="w-[1000px]"
    >
      <template #content>
        <MaintainEntity :mode="mode" :entity="mode !== 'Add' ? selectedEntity : undefined" @submitted="toggleMode(undefined)" />
      </template>
    </TSDialog>
  </div>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import CustomBooleanIconCellRenderer from "@/components/cell-renderers/CustomBooleanIconCellRenderer.vue";
import { useAuthStore } from "@/stores/auth";
import { usePropsStore } from "@/stores/props";
import { type Entity } from "@/types/Entity";
import { getUserPermissions, permissionError } from "@/services/Permissions";
import { UserPermissionsSchema, type UserPermissions } from "@/types/User";
import { entityIsTBD, getEntityDisplay } from "@/services/Utility";
import { getAllEntities } from "@/services/EntitiesService";
import { useConfirm } from "primevue/useconfirm";
import type { MenuItem } from "primevue/menuitem";
import type { GridApi, GridReadyEvent, GridSizeChangedEvent, RowDataUpdatedEvent, RowSelectedEvent, ValueGetterParams } from "ag-grid-community";

const mode = ref<"Add" | "Edit" | "Copy">();
const confirm = useConfirm();
const displayModal = ref(false);
const display = ref(false);

const gridApi = ref<GridApi>();
const searchText = ref("");
const entities = ref<Entity[]>([]);
const selectedEntity = ref<Entity>();

const propsStore = usePropsStore();
const userStore = useAuthStore();
const userPermissions = ref<UserPermissions>(UserPermissionsSchema.parse({}));
const darkVal = computed<boolean>(() => propsStore.darkMode);

//TODO: Import entities

onMounted(async (): Promise<void> => {
  getUserPermissions(userStore.getUserInfo().userId).then((result: UserPermissions) => (userPermissions.value = result));
  populateEntities();
});

const menuItems: MenuItem[] = [
  {
    label: "Edit",
    icon: "pi pi-user-edit",
    visible: () => userPermissions.value.editEntity && !entityIsTBD(selectedEntity.value),
    command: () => toggleMode("Edit"),
  },
  {
    label: "Copy",
    icon: "pi pi-copy",
    visible: () => !entityIsTBD(selectedEntity.value),
    command: () => toggleMode("Copy"),
  },
  {
    label: "Activate",
    icon: "pi pi-power-off",
    visible: () => !selectedEntity.value?.active,
    command: () => toggleEntityActive(true),
  },
  {
    label: "Deactivate",
    icon: "pi pi-power-off",
    visible: () => (selectedEntity.value == null ? false : selectedEntity.value.active),
    command: () => toggleEntityActive(false),
  },
  {
    label: "Delete",
    icon: "pi pi-trash",
    visible: () => selectedEntity.value?.firstName !== "TBD",
    command: () => askDeleteEntity(),
  },
];

const populateEntities = (): void => {
  getAllEntities().then((result: Entity[]) => (entities.value = result));
  selectedEntity.value = undefined;
};

const onFilterTextBoxChanged = (): void => {
  gridApi.value?.setGridOption("quickFilterText", searchText.value);
};

const selectEntity = (event: RowSelectedEvent): void => {
  let node = event.node;

  if (node.isSelected()) {
    selectedEntity.value = node.data;
  }
};

const toggleMode = (value: "Add" | "Edit" | "Copy" | undefined): void => {
  mode.value = value;
  displayModal.value = value != null;

  if (!displayModal.value) {
    populateEntities();
  }
};

const modalClose = (): void => {
  toggleMode(undefined);
  populateEntities();
};

const toggleDisplay = (show: boolean): void => {
  display.value = show;
};

const toggleEntityActive = (active: boolean): void => {
  axios
    .post("/entities/toggle?id=" + selectedEntity.value?.entityId + "&active=" + active)
    .then((response) => {
      populateEntities();
      success("Success", active ? "Entity activated" : "Entity deactivated");
    })
    .catch((e) => errorApi(e));
};

const askDeleteEntity = (): void => {
  if (!selectedEntity.value) {
    info("No Entity Selected", "Select an entity to manage");
    return;
  }

  if (!userPermissions.value.deleteEntity) {
    permissionError();
    return;
  }

  confirmPopup(confirm, "Delete Entity?", "Are you sure that you want to delete this entity?", deleteEntity, () =>
    info("Action cancelled", "Entity not deleted")
  );
};

const deleteEntity = (): any => {
  axios
    .delete("/entities/delete?id=" + selectedEntity.value?.entityId)
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          populateEntities();
          success("Success", "Entity Deleted");
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
};

const resize = (event: GridSizeChangedEvent | RowDataUpdatedEvent): any => {
  event.api.sizeColumnsToFit();
};

const defaultColDefs = {
  filter: true,
  floatingFilter: false,
  cellDataType: "text",
};

const colDefs = ref([
  {
    field: "firstName",
    headerName: "First Name",
    width: 100,
  },
  {
    field: "lastName",
    headerName: "Last Name",
    width: 100,
  },
  {
    field: "companyName",
    headerName: "Company",
    width: 125,
  },
  {
    field: "email",
    headerName: "E-mail",
    width: 125,
  },
  {
    headerName: "Phone",
    valueGetter: (params: ValueGetterParams) => params.data.extension + " " + params.data.entityPhone,
    width: 100,
  },
  {
    field: "user",
    headerName: "User",
    cellDataType: "boolean",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    width: 75,
    cellRenderer: CustomBooleanIconCellRenderer,
  },
  {
    field: "primaryLender",
    headerName: "Primary Lender",
    cellDataType: "boolean",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    width: 75,
    cellRenderer: CustomBooleanIconCellRenderer,
  },
  {
    field: "licensee",
    headerName: "Licensee",
    cellDataType: "boolean",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    width: 75,
    cellRenderer: CustomBooleanIconCellRenderer,
  },
]);
</script>
